import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axiosInstance from "../api/axiosInstance";
import { useAuth } from "../context/AuthContext";

import {
  Table,
  Card,
  Button,
  Space,
  Typography,
  Tag,
  Badge,
  Modal,
  notification,
  Breadcrumb,
  Alert,
  Input,
  Form,
  Select,
  List,
  Avatar,
  Tooltip,
  Tabs,
  Switch,
  Descriptions,
  Collapse,
  Popconfirm,
  Checkbox
} from "antd";
import {
  FaInfoCircle,
  FaEdit,
  FaTrash,
  FaPlus,
  FaUserPlus,
  FaUserMinus,
  FaUsers,
  FaSyncAlt,
  FaLock,
  FaLockOpen,
  FaShieldAlt,
  FaKey,
  FaUserCheck
} from "react-icons/fa";
import "./ManageStaffPage.css";

const { Text, Title } = Typography;
const { Option } = Select;
const { Search } = Input;
const { confirm } = Modal;
const { Panel } = Collapse;

/* ---------- small helper: de-duplicate by id ---------------- */
const dedupById = (arr, idKey = "id") =>
  Array.from(new Map(arr.map((x) => [x[idKey], x])).values());

/* ============================================================ */
const ManageStaffPage = () => {
  /* ------------ state / hooks ------------------------------ */
  const { authData } = useAuth();
  const navigate = useNavigate();

  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);           // users in the selected role
  const [allUsers, setAllUsers] = useState([]);     // all users in hotel
  const [roleUserCounts, setRoleUserCounts] = useState({}); // cached counts per role

  const [loading, setLoading] = useState({
    roles: false,
    users: false,
    permissions: false,
    rolePermissions: false,
    userPermissions: false
  });

  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);

  const [addRoleModalVisible, setAddRoleModalVisible] = useState(false);
  const [addUserModalVisible, setAddUserModalVisible] = useState(false);
  const [editRoleModalVisible, setEditRoleModalVisible] = useState(false);
  const [permissionModalVisible, setPermissionModalVisible] = useState(false);

  const [form] = Form.useForm();
  const [userForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const [permissionForm] = Form.useForm();

  const [searchText, setSearchText] = useState("");
  const [activeTab, setActiveTab] = useState("users");

  const [permissions, setPermissions] = useState([]);
  const [rolePermissions, setRolePermissions] = useState([]);
  const [userPermissions, setUserPermissions] = useState([]);

  /* ------------ breadcrumb ------------------------------- */
  const breadcrumbItems = [
    {
      title: (
        <Link
          to={
            authData?.role?.toLowerCase() === "systemadmin"
              ? "/admin/dashboard"
              : "/hotels/dashboard"
          }
        >
          Home
        </Link>
      ),
    },
    { title: "Staff Management" },
  ];

  /* ------------ data fetching ---------------------------- */
  const fetchRoles = useCallback(async () => {
    if (!authData?.hotelId) return;
    try {
      setLoading(prev => ({ ...prev, roles: true }));
      const { data } = await axiosInstance.get(`/roles/byhotel/${authData.hotelId}`);
      setRoles(
        (data || []).map(r => ({
          id: r.id,
          name: r.name,
          description: r.description,
          hotelId: r.hotelId,
          createdAt: r.createdAt ?? null
        }))
      );
    } catch (err) {
      console.error(err);
      notification.error({
        message: "Error",
        description: "Failed to fetch roles. Please try again later."
      });
    } finally {
      setLoading(prev => ({ ...prev, roles: false }));
    }
  }, [authData?.hotelId]);

  const fetchUsers = useCallback(async () => {
    if (!authData?.hotelId) return;
    try {
      setLoading(prev => ({ ...prev, users: true }));
      const { data } = await axiosInstance.get(`/User?hotelId=${authData.hotelId}`);
      setAllUsers(data || []);
    } catch (err) {
      console.error(err);
      notification.error({
        message: "Error",
        description: "Failed to fetch users. Please try again later."
      });
    } finally {
      setLoading(prev => ({ ...prev, users: false }));
    }
  }, [authData?.hotelId]);

  const fetchPermissions = useCallback(async () => {
    if (!authData?.hotelId) return;
    try {
      setLoading(prev => ({ ...prev, permissions: true }));
      const { data } = await axiosInstance.get(`/RolePermission/Permissions?hotelId=${authData.hotelId}`);
      setPermissions(data || []);
    } catch (err) {
      console.error(err);
      notification.error({
        message: "Error",
        description: "Failed to fetch permissions. Please try again later."
      });
    } finally {
      setLoading(prev => ({ ...prev, permissions: false }));
    }
  }, [authData?.hotelId]);

  const fetchRolePermissions = useCallback(async (roleId) => {
    if (!roleId) return;
    try {
      setLoading(prev => ({ ...prev, rolePermissions: true }));
      const { data } = await axiosInstance.get(`/RolePermission/Roles/${roleId}/Permissions`);
      setRolePermissions((data || []).map(p => p.permissionId));
    } catch (err) {
      notification.error({
        message: "Error",
        description: "Failed to load role permissions"
      });
      setRolePermissions([]);
    } finally {
      setLoading(prev => ({ ...prev, rolePermissions: false }));
    }
  }, []);

  const fetchUserPermissions = useCallback(async (userId) => {
    if (!userId) return;
    try {
      setLoading(prev => ({ ...prev, userPermissions: true }));
      const { data } = await axiosInstance.get(`/RolePermission/Users/${userId}/Permissions`);
      setUserPermissions((data || []).map(p => p.permissionId));
    } catch (err) {
      notification.error({
        message: "Error",
        description: "Failed to load user permissions"
      });
      setUserPermissions([]);
    } finally {
      setLoading(prev => ({ ...prev, userPermissions: false }));
    }
  }, []);

  // NEW: authoritative membership fetch
  const fetchUsersForRole = useCallback(async (roleId) => {
  if (!roleId) return [];
  try {
    const { data } = await axiosInstance.get(`/roles/${roleId}/users`);
    return Array.isArray(data) ? data : [];
  } catch (err) {
    if (err?.response?.status === 405 || err?.response?.status === 404) {
      const { data } = await axiosInstance.get(`/roles/WithUsers`, {
        params: { hotelId: authData.hotelId }
      });
      const role = (data || []).find(r => r.roleId === roleId);
      return role?.users?.map(u => ({
        id: u.userId,
        firstName: u.firstName,
        lastName: u.lastName,
        email: u.email,
        isActive: u.isActive
      })) || [];
    }
    throw err;
  }
}, [authData?.hotelId]);


  // Initial data load
  useEffect(() => {
    (async () => {
      await Promise.all([fetchRoles(), fetchUsers(), fetchPermissions()]);
    })();
  }, [fetchRoles, fetchUsers, fetchPermissions]);

  /* ------------ helpers ---------------------------------- */
  const handleRoleSelect = useCallback(async (role) => {
    if (!role) return;
    setSelectedRole(role);
    setSelectedUser(null);

    // Authoritative list of users who are in this role
    const rUsers = await fetchUsersForRole(role.id);
    setUsers(rUsers);
    setRoleUserCounts(prev => ({ ...prev, [role.id]: rUsers.length }));

    // Load permissions for this role
    await fetchRolePermissions(role.id);
  }, [fetchRolePermissions, fetchUsersForRole]);

  const handleUserSelect = useCallback(async (user) => {
    if (!user) return;
    setSelectedUser(user);
    await fetchUserPermissions(user.id);
  }, [fetchUserPermissions]);

  const refreshAll = useCallback(async () => {
    await Promise.all([fetchRoles(), fetchUsers(), fetchPermissions()]);
    if (selectedRole) {
      const rUsers = await fetchUsersForRole(selectedRole.id);
      setUsers(rUsers);
      setRoleUserCounts(prev => ({ ...prev, [selectedRole.id]: rUsers.length }));
      await fetchRolePermissions(selectedRole.id);
    }
    if (selectedUser) {
      await fetchUserPermissions(selectedUser.id);
    }
  }, [
    fetchRoles,
    fetchUsers,
    fetchPermissions,
    fetchRolePermissions,
    fetchUserPermissions,
    fetchUsersForRole,
    selectedRole,
    selectedUser
  ]);

  /* ------------ CRUD: Roles ------------------------------ */
  const handleAddRole = async (vals) => {
    try {
      const { data: newRole } = await axiosInstance.post("/roles", {
        name: vals.name,
        description: vals.description,
        hotelId: authData.hotelId,
      });

      notification.success({ message: "Role created successfully" });
      setAddRoleModalVisible(false);
      form.resetFields();
      await fetchRoles();
      handleRoleSelect({
        id: newRole.id,
        name: newRole.name,
        description: newRole.description,
        hotelId: newRole.hotelId,
        createdAt: newRole.createdAt ?? null,
      });
    } catch (err) {
      notification.error({
        message: "Error",
        description: err.response?.data?.message || "Failed to create role",
      });
    }
  };

  const handleUpdateRole = async (vals) => {
    if (!selectedRole) return;
    try {
      await axiosInstance.put(`/roles/${selectedRole.id}`, {
        name: vals.name,
        description: vals.description,
      });

      notification.success({ message: "Role updated successfully" });
      setEditRoleModalVisible(false);
      editForm.resetFields();
      await fetchRoles();
    } catch (err) {
      notification.error({
        message: "Error",
        description: err.response?.data?.message || "Failed to update role",
      });
    }
  };

  const handleDeleteRole = (roleId) => {
    confirm({
      title: "Delete Role",
      content:
        "Are you sure you want to delete this role? Users assigned to this role will be unassigned.",
      okText: "Delete",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await axiosInstance.delete(`/roles/${roleId}`);
          notification.success({ message: "Role deleted successfully" });

          if (selectedRole?.id === roleId) {
            setSelectedRole(null);
            setUsers([]);
            setRolePermissions([]);
          }
          await fetchRoles();
        } catch (err) {
          notification.error({
            message: "Error",
            description:
              err.response?.data?.message || "Failed to delete role",
          });
        }
      },
    });
  };

  /* ------------ CRUD: Users within a role ---------------- */
  const handleAddUserToRole = async (vals) => {
    if (!selectedRole || !vals.userId) return;
    try {
      await axiosInstance.post(`/roles/${selectedRole.id}/users`, {
        userId: vals.userId,
      });
      notification.success({ message: "User added to role successfully" });
      setAddUserModalVisible(false);
      userForm.resetFields();

      await fetchUsers(); // refresh global list (optional)
      const rUsers = await fetchUsersForRole(selectedRole.id); // refresh membership
      setUsers(rUsers);
      setRoleUserCounts(prev => ({ ...prev, [selectedRole.id]: rUsers.length }));
    } catch (err) {
      notification.error({
        message: "Error",
        description: err.response?.data?.message || "Failed to add user",
      });
    }
  };

  const handleRemoveUserFromRole = (userId) => {
    confirm({
      title: "Remove User",
      content: "Remove this user from the role?",
      okText: "Remove",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await axiosInstance.delete(`/roles/RemoveUser`, {
            params: { roleId: selectedRole.id, userId },
          });
          notification.success({ message: "User removed successfully" });

          await fetchUsers(); // refresh global list (optional)
          const rUsers = await fetchUsersForRole(selectedRole.id);
          setUsers(rUsers);
          setRoleUserCounts(prev => ({ ...prev, [selectedRole.id]: rUsers.length }));
        } catch (err) {
          notification.error({
            message: "Error",
            description:
              err.response?.data?.message || "Failed to remove user",
          });
        }
      },
    });
  };

  /* ------------ User Permissions ------------------------- */
  const toggleUserPermission = async (permissionId, checked) => {
    if (!selectedUser) {
      return notification.warning({ message: 'Select a user first' });
    }
    try {
      setLoading(prev => ({ ...prev, userPermissions: true }));
      if (checked) {
        await axiosInstance.post(
          `/RolePermission/Users/${selectedUser.id}/Permissions`,
          { permissionIds: [permissionId] }
        );
        setUserPermissions(prev => [...prev, permissionId]);
      } else {
        await axiosInstance.delete(
          `/RolePermission/Users/${selectedUser.id}/Permissions/${permissionId}`
        );
        setUserPermissions(prev => prev.filter(id => id !== permissionId));
      }
    } catch (err) {
      notification.error({
        message: "Error",
        description: err.response?.data?.message || "Failed to update permission",
      });
    } finally {
      setLoading(prev => ({ ...prev, userPermissions: false }));
    }
  };

  const bulkAssignUserPermissions = async (ids) => {
    if (!selectedUser) return;
    try {
      setLoading(prev => ({ ...prev, userPermissions: true }));
      await axiosInstance.post(
        `/RolePermission/Users/${selectedUser.id}/Permissions`,
        { permissionIds: ids }
      );
      setUserPermissions(ids);
      notification.success({ message: "Permissions updated successfully" });
    } catch (err) {
      notification.error({
        message: "Error",
        description: err.response?.data?.message || "Failed to update permissions",
      });
    } finally {
      setLoading(prev => ({ ...prev, userPermissions: false }));
    }
  };

  /* ------------ User Status ------------------------------ */
  const handleToggleUserStatus = async (userId, isActive) => {
    try {
      await axiosInstance.put(`/User/${userId}/activate`, null, {
        params: { isActive: !isActive },
      });
      notification.success({
        message: `User ${isActive ? "deactivated" : "activated"} successfully`,
      });
      await fetchUsers();
    } catch (err) {
      notification.error({
        message: "Error",
        description:
          err.response?.data?.message ||
          `Failed to ${isActive ? "deactivate" : "activate"} user`,
      });
    }
  };

  /* ------------ Role Permissions ------------------------- */
  const handleUpdatePermissions = async (vals) => {
    if (!selectedRole) return;
    try {
      setLoading(prev => ({ ...prev, rolePermissions: true }));
      const currentUser = authData?.userName || "System";
      await axiosInstance.post(
        `/RolePermission/Roles/${selectedRole.id}/Permissions`,
        {
          permissionIds: vals.permissions,
          assignedBy: currentUser
        }
      );
      setRolePermissions(vals.permissions);
      notification.success({ message: "Permissions updated successfully" });
      setPermissionModalVisible(false);
      permissionForm.resetFields();
    } catch (err) {
      notification.error({
        message: "Error",
        description: err.response?.data?.message || "Failed to update permissions",
      });
    } finally {
      setLoading(prev => ({ ...prev, rolePermissions: false }));
    }
  };

  const toggleRolePermission = async (permissionId, checked) => {
    if (!selectedRole) {
      return notification.warning({ message: 'Select a role first' });
    }
    try {
      setLoading(prev => ({ ...prev, rolePermissions: true }));
      const currentUser = authData?.userName || "System";
      if (checked) {
        await axiosInstance.post(
          `/RolePermission/Roles/${selectedRole.id}/Permissions`,
          {
            permissionIds: [permissionId],
            assignedBy: currentUser
          }
        );
        setRolePermissions(prev => [...prev, permissionId]);
      } else {
        await axiosInstance.delete(
          `/RolePermission/Roles/${selectedRole.id}/Permissions/${permissionId}`
        );
        setRolePermissions(prev => prev.filter(id => id !== permissionId));
      }
    } catch (err) {
      notification.error({
        message: "Error",
        description: err.response?.data?.message || "Failed to update permission",
      });
    } finally {
      setLoading(prev => ({ ...prev, rolePermissions: false }));
    }
  };

  const bulkAssignRolePermissions = async (ids) => {
    if (!selectedRole) return;
    try {
      setLoading(prev => ({ ...prev, rolePermissions: true }));
      const currentUser = authData?.userName || "System";
      await axiosInstance.post(
        `/RolePermission/Roles/${selectedRole.id}/Permissions`,
        {
          permissionIds: ids,
          assignedBy: currentUser
        }
      );
      setRolePermissions(ids);
      notification.success({ message: "Permissions updated successfully" });
    } catch (err) {
      notification.error({
        message: "Error",
        description: err.response?.data?.message || "Failed to update permissions",
      });
    } finally {
      setLoading(prev => ({ ...prev, rolePermissions: false }));
    }
  };

  /* ------------ derived data ----------------------------- */
  const filteredRoles = roles.filter((r) =>
    r.name?.toLowerCase().includes(searchText.toLowerCase())
  );

  // Build "available users" by set difference: allUsers minus users currently assigned to the selected role
  const assignedIds = new Set(users.map(u => String(u.id)));
  const availableUsers = dedupById(
    allUsers.filter(u => u.id && !assignedIds.has(String(u.id))),
    "id"
  );

  /* ------------ columns / tab config --------------------- */
  const roleColumns = [
    {
      title: "Role Name",
      dataIndex: "name",
      key: "name",
      render: (text, record) => (
        <Button
          type="link"
          onClick={() => handleRoleSelect(record)}
          style={{
            fontWeight: selectedRole?.id === record.id ? "bold" : "normal",
          }}
        >
          {text || "Unnamed Role"}
        </Button>
      ),
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      render: (txt) => txt || "N/A",
    },
    {
      title: "User Count",
      key: "userCount",
      render: (_, record) => {
        // Prefer authoritative cached count if we have it; fall back to 0
        const count = typeof roleUserCounts[record.id] === "number"
          ? roleUserCounts[record.id]
          : (selectedRole?.id === record.id ? users.length : 0);
        return (
          <Badge
            count={count}
            showZero
            style={{ backgroundColor: "#1890ff" }}
          />
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, rec) => (
        <Space>
          <Tooltip title="Edit Role">
            <Button
              icon={<FaEdit />}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedRole(rec);
                editForm.setFieldsValue({
                  name: rec.name,
                  description: rec.description,
                });
                setEditRoleModalVisible(true);
              }}
            />
          </Tooltip>
          <Tooltip title="Manage Permissions">
            <Button
              icon={<FaShieldAlt />}
              onClick={async (e) => {
                e.stopPropagation();
                setSelectedRole(rec);
                await handleRoleSelect(rec); // ensure we have latest membership/permissions
                setPermissionModalVisible(true);
              }}
            />
          </Tooltip>
          <Tooltip title="Delete Role">
            <Button
              danger
              icon={<FaTrash />}
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteRole(rec.id);
              }}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const permissionColumns = [
    {
      title: "Permission",
      dataIndex: "name",
      render: (text, record) => (
        <Space>
          <FaKey
            style={{
              color: rolePermissions.includes(record.permissionId)
                ? "#52c41a"
                : "#ff4d4f"
            }}
          />
          <Text>{text}</Text>
        </Space>
      )
    },
    {
      title: "Category",
      dataIndex: "category",
      render: (text) => <Tag>{text || "General"}</Tag>
    },
    {
      title: "Status",
      render: (_, record) => (
        <Tag color={rolePermissions.includes(record.permissionId) ? "green" : "red"}>
          {rolePermissions.includes(record.permissionId) ? "Assigned" : "Unassigned"}
        </Tag>
      )
    },
    {
      title: "Action",
      render: (_, record) => (
        <Checkbox
          checked={rolePermissions.includes(record.permissionId)}
          onChange={(e) => toggleRolePermission(record.permissionId, e.target.checked)}
          disabled={!selectedRole || loading.rolePermissions}
        />
      )
    }
  ];

  const userPermissionColumns = [
    {
      title: "Permission",
      dataIndex: "name",
      render: (text, record) => (
        <Space>
          <FaKey
            style={{
              color: userPermissions.includes(record.permissionId)
                ? "#52c41a"
                : "#ff4d4f"
            }}
          />
          <Text>{text}</Text>
        </Space>
      )
    },
    {
      title: "Category",
      dataIndex: "category",
      render: (text) => <Tag>{text || "General"}</Tag>
    },
    {
      title: "Status",
      render: (_, record) => (
        <Tag color={userPermissions.includes(record.permissionId) ? "green" : "red"}>
          {userPermissions.includes(record.permissionId) ? "Assigned" : "Unassigned"}
        </Tag>
      )
    },
    {
      title: "Action",
      render: (_, record) => (
        <Checkbox
          checked={userPermissions.includes(record.permissionId)}
          onChange={(e) => toggleUserPermission(record.permissionId, e.target.checked)}
          disabled={!selectedUser || loading.userPermissions}
        />
      )
    }
  ];

  const tabItems = [
    {
      key: "users",
      label: (
        <span>
          <FaUsers /> Users
        </span>
      ),
      children:
        users.length > 0 ? (
          <List
            dataSource={users}
            renderItem={(u) => (
              <List.Item
                key={u.id}
                actions={[
                  <Tooltip
                    key="status"
                    title={u.isActive ? "Deactivate" : "Activate"}
                  >
                    <Switch
                      checked={u.isActive}
                      onChange={() => handleToggleUserStatus(u.id, u.isActive)}
                      checkedChildren={<FaLockOpen />}
                      unCheckedChildren={<FaLock />}
                    />
                  </Tooltip>,
                  <Tooltip key="remove" title="Remove from role">
                    <Button
                      danger
                      icon={<FaUserMinus />}
                      onClick={() => handleRemoveUserFromRole(u.id)}
                    />
                  </Tooltip>,
                  <Tooltip key="permissions" title="Manage permissions">
                    <Button
                      icon={<FaKey />}
                      onClick={() => handleUserSelect(u)}
                    />
                  </Tooltip>
                ]}
              >
                <List.Item.Meta
                  avatar={<Avatar>{u.firstName?.[0] || "U"}</Avatar>}
                  title={`${u.firstName || ""} ${u.lastName || ""}`}
                  description={
                    <Space direction="vertical" size={0}>
                      <Text>{u.email || "No email"}</Text>
                      <Text type="secondary">
                        {u.isActive ? (
                          <Tag color="green">Active</Tag>
                        ) : (
                          <Tag color="red">Inactive</Tag>
                        )}
                      </Text>
                    </Space>
                  }
                />
              </List.Item>
            )}
          />
        ) : (
          <Alert
            message="No users assigned to this role"
            type="info"
            showIcon
            action={
              <Button
                type="primary"
                icon={<FaUserPlus />}
                onClick={() => setAddUserModalVisible(true)}
                disabled={availableUsers.length === 0}
              >
                Assign Users
              </Button>
            }
          />
        ),
    },
    {
      key: "permissions",
      label: (
        <span>
          <FaShieldAlt /> Permissions
        </span>
      ),
      children: (
        <div>
          <Space style={{ marginBottom: 16 }}>
            <Button
              type="primary"
              onClick={() => bulkAssignRolePermissions(permissions.map(p => p.permissionId))}
              loading={loading.rolePermissions}
            >
              Assign All Permissions
            </Button>
            <Button
              danger
              onClick={async () => {
                try {
                  setLoading(prev => ({ ...prev, rolePermissions: true }));
                  await axiosInstance.delete(
                    `/RolePermission/Roles/${selectedRole.id}/Permissions`
                  );
                  setRolePermissions([]);
                  notification.success({ message: "Permissions cleared" });
                } catch (err) {
                  notification.error({
                    message: "Error",
                    description: err.response?.data?.message || "Failed to clear permissions",
                  });
                } finally {
                  setLoading(prev => ({ ...prev, rolePermissions: false }));
                }
              }}
              loading={loading.rolePermissions}
            >
              Clear All Permissions
            </Button>
          </Space>

          <Table
            rowKey="permissionId"
            columns={permissionColumns}
            dataSource={permissions}
            loading={loading.permissions || loading.rolePermissions}
            pagination={{ pageSize: 10 }}
            scroll={{ x: true }}
          />
        </div>
      ),
    },
    {
      key: "details",
      label: (
        <span>
          <FaInfoCircle /> Details
        </span>
      ),
      children: (
        <Descriptions bordered column={1}>
          <Descriptions.Item label="Role Name">
            {selectedRole?.name || "N/A"}
          </Descriptions.Item>
          <Descriptions.Item label="Description">
            {selectedRole?.description || "No description"}
          </Descriptions.Item>
          <Descriptions.Item label="Created At">
            {selectedRole?.createdAt
              ? new Date(selectedRole.createdAt).toLocaleString()
              : "N/A"}
          </Descriptions.Item>
          <Descriptions.Item label="User Count">
            <Badge
              count={users.length}
              showZero
              style={{ backgroundColor: "#1890ff" }}
            />
          </Descriptions.Item>
          <Descriptions.Item label="Permission Count">
            <Badge
              count={rolePermissions.length}
              showZero
              style={{ backgroundColor: "#52c41a" }}
            />
          </Descriptions.Item>
        </Descriptions>
      ),
    },
  ];

  /* ------------ render ----------------------------------- */
  return (
    <div className="role-management-page">
      <Breadcrumb items={breadcrumbItems} style={{ marginBottom: 16 }} />

      <Card
        title={
          <Space>
            <Title level={4} style={{ margin: 0 }}>
              Staff Management
            </Title>
            <Badge
              count={roles.length}
              showZero
              style={{ backgroundColor: "#1890ff" }}
            />
          </Space>
        }
        extra={
          <Space>
            <Search
              placeholder="Search roles"
              allowClear
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 200 }}
            />
            <Button
              type="primary"
              icon={<FaPlus />}
              onClick={() => setAddRoleModalVisible(true)}
            >
              Add Role
            </Button>
            <Button icon={<FaSyncAlt />} onClick={refreshAll} loading={loading.roles || loading.users}>
              Refresh
            </Button>
          </Space>
        }
      >
        <div className="role-management-container">
          {/* LEFT – role list */}
          <div className="role-list-section">
            <Table
              rowKey="id"
              columns={roleColumns}
              dataSource={filteredRoles}
              loading={loading.roles}
              pagination={false}
              scroll={{ y: 500 }}
              onRow={(rec) => ({ onClick: () => handleRoleSelect(rec) })}
              rowClassName={(rec) =>
                selectedRole?.id === rec.id ? "selected-row" : ""
              }
              locale={{
                emptyText: (
                  <Text type="secondary">
                    No roles found. Create your first role.
                  </Text>
                ),
              }}
            />
          </div>

          {/* RIGHT – details */}
          <div className="role-details-section">
            {selectedUser ? (
              <Card
                title={
                  <Space>
                    <Text strong>{selectedUser.firstName} {selectedUser.lastName}</Text>
                    <Button
                      icon={<FaUserCheck />}
                      onClick={() => setSelectedUser(null)}
                    >
                      Back to Role
                    </Button>
                  </Space>
                }
                extra={
                  <Space>
                    <Button
                      type="primary"
                      onClick={() => bulkAssignUserPermissions(permissions.map(p => p.permissionId))}
                      loading={loading.userPermissions}
                    >
                      Assign All
                    </Button>
                    <Button
                      danger
                      onClick={async () => {
                        try {
                          setLoading(prev => ({ ...prev, userPermissions: true }));
                          await axiosInstance.delete(
                            `/RolePermission/Users/${selectedUser.id}/Permissions`
                          );
                          setUserPermissions([]);
                          notification.success({ message: "Permissions cleared" });
                        } catch (err) {
                          notification.error({
                            message: "Error",
                            description: err.response?.data?.message || "Failed to clear permissions",
                          });
                        } finally {
                          setLoading(prev => ({ ...prev, userPermissions: false }));
                        }
                      }}
                      loading={loading.userPermissions}
                    >
                      Clear All
                    </Button>
                  </Space>
                }
              >
                <Table
                  rowKey="permissionId"
                  columns={userPermissionColumns}
                  dataSource={permissions}
                  loading={loading.permissions || loading.userPermissions}
                  pagination={{ pageSize: 10 }}
                  scroll={{ x: true }}
                />
              </Card>
            ) : selectedRole ? (
              <Card
                title={
                  <Space>
                    <Text strong>{selectedRole.name || "Role"}</Text>
                    <Badge
                      count={users.length}
                      showZero
                      style={{ backgroundColor: "#1890ff" }}
                    />
                  </Space>
                }
                extra={
                  <Button
                    type="primary"
                    icon={<FaUserPlus />}
                    onClick={() => setAddUserModalVisible(true)}
                    disabled={availableUsers.length === 0}
                  >
                    Add User
                  </Button>
                }
              >
                <Tabs
                  activeKey={activeTab}
                  onChange={setActiveTab}
                  items={tabItems}
                  style={{ marginTop: -16 }}
                />
              </Card>
            ) : (
              <Card>
                <Alert
                  message="Select a role to view details"
                  type="info"
                  showIcon
                />
              </Card>
            )}
          </div>
        </div>

        {/* ---------- Add Role Modal ---------- */}
        <Modal
          title="Add New Role"
          open={addRoleModalVisible}
          onCancel={() => {
            setAddRoleModalVisible(false);
            form.resetFields();
          }}
          footer={null}
        >
          <Form form={form} layout="vertical" onFinish={handleAddRole}>
            <Form.Item
              name="name"
              label="Role Name"
              rules={[{ required: true, message: "Please input role name!" }]}
            >
              <Input placeholder="e.g., Housekeeping Manager" />
            </Form.Item>
            <Form.Item name="description" label="Description">
              <Input.TextArea rows={3} placeholder="Role description" />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit">
                Create Role
              </Button>
            </Form.Item>
          </Form>
        </Modal>

        {/* ---------- Edit Role Modal ---------- */}
        {selectedRole && (
          <Modal
            title={`Edit Role: ${selectedRole.name}`}
            open={editRoleModalVisible}
            onCancel={() => {
              setEditRoleModalVisible(false);
              editForm.resetFields();
            }}
            footer={null}
          >
            <Form form={editForm} layout="vertical" onFinish={handleUpdateRole}>
              <Form.Item
                name="name"
                label="Role Name"
                rules={[{ required: true, message: "Please input role name!" }]}
              >
                <Input placeholder="e.g., Housekeeping Manager" />
              </Form.Item>
              <Form.Item name="description" label="Description">
                <Input.TextArea rows={3} placeholder="Role description" />
              </Form.Item>
              <Form.Item>
                <Button type="primary" htmlType="submit">
                  Update Role
                </Button>
              </Form.Item>
            </Form>
          </Modal>
        )}

        {/* ---------- Add User Modal ---------- */}
        {selectedRole && (
          <Modal
            title={`Add User to ${selectedRole.name}`}
            open={addUserModalVisible}
            onCancel={() => {
              setAddUserModalVisible(false);
              userForm.resetFields();
            }}
            footer={null}
          >
            <Form
              form={userForm}
              layout="vertical"
              onFinish={handleAddUserToRole}
            >
              <Form.Item
                name="userId"
                label="Select User"
                rules={[{ required: true, message: "Please select a user!" }]}
              >
                <Select
                  placeholder="Select a user"
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, opt) =>
                    opt?.children?.toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {availableUsers.map((u, idx) => (
                    <Option key={`${u.id}-${idx}`} value={u.id}>
                      {`${u.firstName || ""} ${u.lastName || ""} (${u.email || "no email"})`}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item>
                <Button type="primary" htmlType="submit">
                  Add to Role
                </Button>
              </Form.Item>
            </Form>
          </Modal>
        )}

        {/* ---------- Manage Permissions Modal ---------- */}
        {selectedRole && (
          <Modal
            title={`Manage Permissions for ${selectedRole.name}`}
            open={permissionModalVisible}
            onCancel={() => {
              setPermissionModalVisible(false);
              permissionForm.resetFields();
            }}
            footer={null}
            width={800}
          >
            <Form
              form={permissionForm}
              layout="vertical"
              onFinish={handleUpdatePermissions}
              initialValues={{ permissions: rolePermissions }}
            >
              <Form.Item name="permissions" label="Select Permissions">
                <Select mode="multiple" placeholder="Select permissions" style={{ width: "100%" }}>
                  {permissions.map((p) => (
                    <Option key={p.permissionId} value={p.permissionId}>
                      {p.name} — {p.category || "No category"}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item>
                <Button type="primary" htmlType="submit">
                  Update Permissions
                </Button>
              </Form.Item>
            </Form>
          </Modal>
        )}
      </Card>
    </div>
  );
};

export default ManageStaffPage;
